// src/components/OPDDashboard.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Users, Clock, Newspaper, Search, Filter, Globe, Database, X, Calendar, User } from "lucide-react";
import axios from "axios";
import { useLanguageContext } from "../navigations/homenav";

const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

// Canonical codes for counting; labels will be translated separately
const disabilityCodes = [
  'visual',
  'hearing',
  'physical',
  'intellectual',
  'autism',
  'mental',
  'chronic',
  'speech',
  'other',
];

const genderCodes = ['male', 'female'];

function getAge(dob) {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const diffMs = Date.now() - birthDate.getTime();
  const ageDt = new Date(diffMs);
  return Math.abs(ageDt.getUTCFullYear() - 1970);
}

export default function OPDDashboard() {
  const { t } = useLanguageContext();
  const [totalMembers, setTotalMembers] = useState(0);
  const [members, setMembers] = useState([]);
  const [opdName, setOpdName] = useState("");

  // --- News States ---
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [newsSource, setNewsSource] = useState("local"); // "ET", "WORLD", or "local"
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedNews, setSelectedNews] = useState(null); // For modal
  const [showModal, setShowModal] = useState(false);

  const PAGE_SIZE = 4;

  // --- Fetch Members (align exactly with MembersPage.js filtering) ---
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const currentUserEmail = currentUser?.email;

    if (!currentUserEmail) {
      alert("No logged-in user found.");
      return;
    }

    const fetchMembers = async () => {
      try {
        // 1) Get OPD profile to determine sub city and name
        let subCity = '';
        try {
          const profRes = await fetch(`${API_BASE_URL}/get_opd_by_email.php?email=${encodeURIComponent(currentUserEmail)}`);
          const profData = await profRes.json();
          if (profData?.status === 'success' && profData?.data?.sub_city) {
            subCity = profData.data.sub_city;
            if (profData.data.name) {
              setOpdName(profData.data.name);
            }
          }
        } catch (_) {}

        // 2) Fetch all members and filter by sub city (and approved), same as MembersPage
        const res = await fetch(`${API_BASE_URL}/getAllMembers.php`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const norm = (v) => (v ?? '').toString().trim().toLowerCase();
        const subCityNorm = norm(subCity);
        const bySubCity = subCity ? list.filter(m => norm(m.subCity) === subCityNorm) : list;
        const approved = bySubCity.filter(m => (m.aaapd_status || '').toString().toLowerCase() === 'approved');
        setMembers(approved);
        setTotalMembers(approved.length);
      } catch (error) {
        console.error("Error fetching members:", error);
        alert("Failed to load dashboard data");
        setMembers([]);
        setTotalMembers(0);
      }
    };

    fetchMembers();
  }, []);

  // --- Members data processing ---
  const membersByDisability = disabilityCodes.map((code) => {
    return members.filter((m) => {
      const memberTypes = (m.disabilityType || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      
      // Map Amharic disability types to codes
      return memberTypes.some((mt) => {
        const normalizedMt = mt.trim().toLowerCase();
        switch(code) {
          case 'visual':
            return normalizedMt.includes('ዓይነ ስውር');
          case 'hearing':
            return normalizedMt.includes('መስማት የተሳነው');
          case 'physical':
            return normalizedMt.includes('የአካል') || normalizedMt.includes('መንቀሳቀስ');
          case 'intellectual':
            return normalizedMt.includes('የአዕምሮ እድገት');
          case 'autism':
            return normalizedMt.includes('ኦቲዝም') || normalizedMt.includes('ነርቭ');
          case 'mental':
            return normalizedMt.includes('የአእምሮ ጤና');
          case 'chronic':
            return normalizedMt.includes('ሥር የሰደደ');
          case 'speech':
            return normalizedMt.includes('የመናገር');
          case 'other':
            return normalizedMt === 'የተለየ';
          default:
            return false;
        }
      });
    }).length;
  });

  // Normalize gender to canonical codes to support EN/AM and variants
  const normalizeGender = (val) => {
    const s = (val || '').toString().trim().toLowerCase();
    // Amharic: ወንድ (male), ሴት (female)
    if (s === 'ወንድ') return 'male';
    if (s === 'ሴት') return 'female';
    if (['m', 'male', 'man', 'boy'].includes(s)) return 'male';
    if (['f', 'female', 'woman', 'girl'].includes(s)) return 'female';
    return s; // fallback to original lowercased value
  };

  const membersByGender = genderCodes.map((g) =>
    members.filter((m) => normalizeGender(m.gender) === g).length
  );

  // Use the explicit age field from members (same as MembersPage Age column)
  const ages = members
    .map((m) => Number(m.age))
    .filter((age) => Number.isFinite(age) && age > 0);
  const averageAge =
    ages.length > 0
      ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length)
      : "-";

  // Localized labels
  const disabilityLabels = [
    t('disTypeVisual'),
    t('disTypeHearing'),
    t('disTypePhysical'),
    t('disTypeIntellectual'),
    t('disTypeAutism') || 'Autism/Neurodivergence',
    t('disTypeMentalHealth'),
    t('disTypeChronicIllness'),
    t('disTypeSpeech'),
    t('disTypeOther'),
  ];
  const genderLabels = [t('male'), t('female')];

  const disabilityData = {
    labels: disabilityLabels,
    datasets: [
      {
        data: membersByDisability,
        backgroundColor: [
          "#004d40", // Dark Teal
          "#00796b", // Teal
          "#009688", // Light Teal
          "#4db6ac", // Lighter Teal
          "#80cbc4", // Even Lighter Teal
          "#b2dfdb", // Soft Teal
          "#e0f2f1", // Very Soft Teal
          "#a7ffeb", // Mint Teal
        ],
        borderColor: "#ffffff",
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const genderData = {
    labels: genderLabels,
    datasets: [
      {
        label: t('numberOfMembers'),
        data: membersByGender,
        backgroundColor: ["#00796b", "#80cbc4"],
        borderRadius: 4,
      },
    ],
  };

  // --- Fetch News ---
  const fetchNews = async () => {
    setLoading(true);
    setError("");
    try {
      if (newsSource === "local") {
        // Fetch from local backend (same as NewsPage)
        const response = await axios.get("https://aaapd.org.et/gallery_backend/news/getNews.php");
        if (response.data && response.data.length > 0) {
          setArticles(response.data);
          setTotalResults(response.data.length);
        } else {
          setArticles([]);
          setTotalResults(0);
          setError("No local news found.");
        }
      } else {
        // Fetch from external NewsAPI
        // Fetch from backend proxy (server-side NewsAPI)
        const NEWS_BACKEND = "https://aaapd.org.et/gallery_backend/api/news.php";
        let baseQ =
          search ||
           '"persons with disabilities" OR disability OR "disability rights" OR "inclusive education" OR accessibility';
        if (newsSource === "ET") baseQ += " AND Ethiopia";

        const url = `${NEWS_BACKEND}?q=${encodeURIComponent(baseQ)}&sortBy=publishedAt&language=en&pageSize=${PAGE_SIZE}&page=${page}`;
        const res = await fetch(url);
        const json = await res.json();

        const raw = Array.isArray(json)
          ? json
          : Array.isArray(json.articles)
          ? json.articles
          : Array.isArray(json.data)
          ? json.data
          : [];

        let sortedArticles = raw;

        if (newsSource === "WORLD") {
          sortedArticles = [
            ...raw.filter((a) =>
              ((a.source && (a.source.name || a.source)) || "").toString().toLowerCase().includes("ethiopia") ||
              ((a.title || "") + " " + (a.description || a.content || "")).toLowerCase().includes("ethiopia")
            ),
            ...raw.filter((a) =>
              !(((a.source && (a.source.name || a.source)) || "").toString().toLowerCase().includes("ethiopia") ||
              ((a.title || "") + " " + (a.description || a.content || "")).toLowerCase().includes("ethiopia"))
            ),
          ];
        }

        setArticles(sortedArticles);
        setTotalResults(raw.length);
      }
    } catch (err) {
      setError(err.message);
      setArticles([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [page, newsSource]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchNews();
  };

  const openNewsModal = (article) => {
    if (newsSource === "local") {
      setSelectedNews(article);
      setShowModal(true);
    }
    
  };

  const closeNewsModal = () => {
    setShowModal(false);
    setSelectedNews(null);
  };

  const StatCard = ({ icon, title, value, colorClass, children }) => (
    <div className={`bg-white p-6 rounded-xl shadow-md border-l-4 ${colorClass} flex items-center gap-6 hover:shadow-lg transition-shadow`}>
      {icon}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{title}</h3>
        {value && <span className="text-3xl font-bold text-gray-800">{value}</span>}
        {children}
      </div>
    </div>
  );

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="p-4 md:p-8">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800">
            {opdName ? `${opdName} ${t('dashboard')}` : t('opdDashboardTitle')}
          </h1>
          
        </header>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: News Section */}
          <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3"><Newspaper className="w-7 h-7 text-teal-600" /> {t('latestDisNews')}</h3>
            </div>

            {/* Search & Filter */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={newsSource}
                  onChange={(e) => {
                    setNewsSource(e.target.value);
                    setPage(1);
                  }}
                  className="w-full sm:w-auto pl-10 pr-3 py-2 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                >
                
                  <option value="local">
                    <Database className="w-4 h-4 inline mr-1" />
                    {t('localNews')}
                  </option>
                  <option value="ET">
                    <Globe className="w-4 h-4 inline mr-1" />
                    {t('ethiopiaNews')}
                  </option>
                  <option value="WORLD">
                    <Globe className="w-4 h-4 inline mr-1" />
                    {t('worldwideNews')}
                  </option>
                 
                </select>
              </div>
              <button
                type="submit"
                className="px-5 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
              >
                {t('search')}
              </button>
            </form>

            {/* News Results */}
            {loading ? (
              <p className="text-center text-gray-500 py-8">{t('loadingNews')}</p>
            ) : error ? (
              <p className="text-center text-red-500 font-medium py-8">{error}</p>
            ) : articles.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('noNewsFound')}</p>
            ) : (
              <div className="flex flex-col gap-4">
                {articles.map((article, idx) => {
                  // Handle both external API and local news formats
                  const isExternalNews = newsSource !== "local";
                  const title = article.title || article.title;
                  const description = isExternalNews
                    ? (article.description || t('noDescription'))
                    : (article.excerpt || article.description || t('noDescription'));
                  const image = article.urlToImage || article.image || "/dashboardnews.png";
                  const link = isExternalNews ? article.url : `/news/${article.id}`;
                  const source = isExternalNews ? (article.source?.name || t('unknownSource')) : t('aaapdNewsSource');
                  const date = isExternalNews
                    ? new Date(article.publishedAt).toLocaleDateString()
                    : article.date;
                  const category = isExternalNews ? t('externalNewsCategory') : (article.category || t('newsCategory'));

                  return (
                    <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow flex flex-col md:flex-row">
                      <img
                        src={image}
                        alt={title}
                        className="w-full h-40 md:w-48 object-cover flex-shrink-0 bg-gray-200"
                      />
                      <div className="p-4 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              isExternalNews
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {category}
                            </span>
                            <span className="text-xs text-gray-500">
                              {source}
                            </span>
                          </div>
                          {isExternalNews ? (
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-lg font-semibold text-teal-700 hover:underline mb-1 cursor-pointer"
                            >
                              {title}
                            </a>
                          ) : (
                            <button
                              onClick={() => openNewsModal(article)}
                              className="block text-lg font-semibold text-teal-700 hover:underline mb-1 text-left cursor-pointer"
                            >
                              {title}
                            </button>
                          )}
                          <p className="text-gray-500 text-xs mb-2">
                            {date}
                          </p>
                          <p className="text-gray-600 text-sm line-clamp-2">
                            {description}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalResults > PAGE_SIZE && (
              <div className="flex justify-center items-center mt-6 gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Prev
                </button>
                <span>
                  Page {page} of {Math.ceil(totalResults / PAGE_SIZE)}
                </span>
                <button
                  disabled={page >= Math.ceil(totalResults / PAGE_SIZE)}
                  onClick={() => setPage(page + 1)}
                  className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* RIGHT: Stats & Charts */}
          <div className="lg:col-span-1 flex flex-col gap-8">
            {/* Stat Cards */}
            <StatCard icon={<Users className="w-10 h-10 text-teal-600" />} title={t('totalMembers')} value={totalMembers} colorClass="border-teal-600" />
            <StatCard icon={<Clock className="w-10 h-10 text-cyan-600" />} title={t('averageAge')} value={averageAge} colorClass="border-cyan-600" />
            <StatCard title={t('genderSplit')} colorClass="border-pink-500" icon={<Users className="w-10 h-10 text-pink-500" />}>
              <div className="flex items-center gap-4 mt-2 text-gray-800 font-bold text-lg">
                <span>{t('male')}: {membersByGender[0]}</span>
                <span>{t('female')}: {membersByGender[1]}</span>
              </div>
            </StatCard>

            {/* Charts */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {t('membersByDisability')}
              </h3>
              <Pie
                data={disabilityData}
                options={{
                  plugins: {
                    legend: { position: 'right' },
                    // Disable numeric value labels on slices, matching AssociationDashboard
                    valueLabels: false,
                    datalabels: { display: false },
                  },
                }}
              />
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {t('membersByGender')}
              </h3>
              <Bar
                data={genderData}
                options={{
                  indexAxis: "y",
                  responsive: true,
                  plugins: { legend: { display: false } },
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* News Detail Modal */}
      {showModal && selectedNews && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto ml-16 md:ml-24">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    {selectedNews.category || t('newsCategory')}
                  </span>
                  <span className="text-sm text-gray-500">{t('aaapdNewsSource')}</span>
                </div>
                <button
                  onClick={closeNewsModal}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="space-y-6">
                {/* Title */}
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                  {selectedNews.title}
                </h2>

                {/* Meta Information */}
                <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-teal-500" />
                    <span>{selectedNews.date}</span>
                  </div>
                  <div className="flex items-center">
                    <User className="w-4 h-4 mr-2 text-teal-500" />
                    <span>{selectedNews.author}</span>
                  </div>
                </div>

                {/* Featured Image - Smaller size */}
                {selectedNews.image && (
                  <div className="w-full">
                    <img
                      src={selectedNews.image}
                      alt={selectedNews.title}
                      className="w-full h-48 md:h-64 object-cover rounded-lg"
                    />
                  </div>
                )}

                {/* Description */}
                <div className="prose prose-lg max-w-none">
                  <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {selectedNews.description || selectedNews.excerpt}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end mt-8 pt-6 border-t">
                <button
                  onClick={closeNewsModal}
                  className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
